import React, { useState } from 'react';
import {
  Box,
  FlexBox,
  Typography,
  Checkbox,
  useTheme,
  useMediaQuery,
  Grid,
  MuiBox,
  MuiTable
} from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { selectors as willCallSelectors, actions as willCallActions, Order } from 'services/willcall';
import { selectors as configSelectors, Configuration, LanguageField, Message } from 'services/configuration';
import { Scrollbar } from 'components/Scrollbar';
import { formatCurrency } from 'utils/stringFormat';
import { Footer } from 'components/Footer';
import { useLocation, useNavigate } from 'react-router-dom';
import { useActions } from 'hooks/useActions';
import { ActionOverlay } from 'components/ActionOverlay';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useStyles } from './styled';
import { useTranslation } from 'react-i18next';

const WillCallOrderListView = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [ordersSelected, setOrdersSelected] = useState<(string | number)[]>([]);
  const [showActionOverlay, setShowActionOverlay] = useState<boolean>(false);
  const customer = useSelector(willCallSelectors.selectCustomer);
  const ordersList = useSelector(willCallSelectors.selectOrdersList);
  const orderDetails = useSelector(willCallSelectors.selectOrderDetails);
  const loading = useSelector(willCallSelectors.selectLoading);
  const error = useSelector(willCallSelectors.selectError);
  const [resetWillCall, resetOrderDetails, getOrder] = useActions([
    willCallActions.resetWillCall,
    willCallActions.resetOrderDetails,
    willCallActions.getOrder
  ]);

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const {
    willCall: { skipTicketList }
  } = config;
  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const messaging: Message = get(config, `messaging.${i18n.language}.orderListPage`, {});
  const support = get(config, `ticketing.supportName.${i18n.language}`) || t('commonText.support');
  const errorMessages = get(config, `willCall.errorMessages`);
  const errorOverlaySubheader =
    get(errorMessages, `subheader.${i18n.language}`) || t('commonText.ticketsTryAgain', { support });

  const classes = useStyles();
  const themes = useTheme();
  const matchPortrait = useMediaQuery(themes.breakpoints.down('lg'));

  const onPreviousClick = () => {
    navigate('/willcall/selection', { state: { from: location.pathname } });
    resetWillCall();
  };

  const onNextClick = () => {
    const orderNumber = ordersSelected.filter((order) => typeof order === 'number');
    const orderCode = ordersSelected.filter((order) => typeof order === 'string');
    const reqObj = {
      orderNumber: orderNumber.length ? orderNumber : undefined,
      orderCode: orderCode.length ? orderCode : undefined
    };
    getOrder(reqObj);
  };

  const toggleOrderSelection = (id: string | number) => {
    if (ordersSelected.includes(id)) {
      setOrdersSelected(ordersSelected.filter((orderId) => orderId.toString() !== id.toString()));
    } else {
      setOrdersSelected(ordersSelected.concat(id));
    }
  };

  const handleTryAgain = () => {
    resetOrderDetails();
  };

  const tableView = () => {
    return (
      <Scrollbar>
        <Box flexOverride="none" padding="0 2">
          <MuiBox className={classes.orderListTableBox}>
            <MuiTable.TableContainer>
              <MuiTable.Table className={classes.tableContainer} stickyHeader>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    {columns.map((column, colIdx) => (
                      <MuiTable.TableCell key={colIdx} className={classes.tableHeaderCell} sx={{ width: column.width }}>
                        {column.header}
                      </MuiTable.TableCell>
                    ))}
                  </MuiTable.TableRow>
                </MuiTable.TableHead>

                <MuiTable.TableBody>
                  {ordersList.map((row: Order, rowIdx: number) => {
                    return (
                      <MuiTable.TableRow
                        key={rowIdx}
                        onClick={() => toggleOrderSelection(row.orderId)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '8px',
                          boxShadow: '0 3px 9px 0 rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        {columns.map((column, colIdx) => (
                          <MuiTable.TableCell
                            key={colIdx}
                            className={classes.tableBodyCell}
                            sx={{ width: column.width }}
                          >
                            {column.cell(row as Order)}
                          </MuiTable.TableCell>
                        ))}
                      </MuiTable.TableRow>
                    );
                  })}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </MuiBox>
        </Box>
      </Scrollbar>
    );
  };

  const columns = [
    {
      header: t('commonText.select'),
      cell: (row: Order) => (
        <FlexBox>
          <Checkbox size="large" checked={ordersSelected.includes(row.orderId)} />
        </FlexBox>
      ),
      width: matchPortrait ? 150 : 200
    },
    {
      header: t('willCallOrderList.orderId'),
      cell: (row: Order) => (
        <MuiBox className={classes.orderListViewColumn}>
          <Typography fontSize={24} fontWeight={600}>
            {row.orderId}
          </Typography>
        </MuiBox>
      )
    },
    {
      header: t('willCallOrderList.orderDateTime'),
      cell: (row: Order) => (
        <MuiBox className={classes.orderListViewColumn}>
          <Typography fontSize={24} fontWeight={600}>
            {t('{{date, weekdayMonthDay}}', { date: new Date(row.orderDate) })} &middot;{' '}
            {t('{{date, time}}', { date: new Date(row.orderDate) })}
          </Typography>
        </MuiBox>
      )
    },
    {
      header: t('willCallOrderList.amountPaid'),
      cell: (row: Order) => (
        <MuiBox className={classes.orderListViewColumn}>
          <Typography fontSize={24} fontWeight={600} sx={{ textAlign: 'right' }}>
            {formatCurrency(row.amount, {}, languageSettings)}
          </Typography>
        </MuiBox>
      ),
      width: matchPortrait ? 200 : 300
    }
  ];

  if (!ordersList || !ordersList.length) onPreviousClick();
  if (!!orderDetails.length) navigate('/willcall/orders');

  return (
    <>
      {loading && <LoadingOverlay message={t('willCallOrderList.pleaseWait')} />}
      {error && !loading && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={910}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {error}
              </Typography>
            </Box>
          }
          description={
            <Box width={710}>
              <Typography fontSize={30} align="center">
                {errorOverlaySubheader}
              </Typography>
            </Box>
          }
          confirmText={t('commonText.tryAgain')}
          onConfirm={handleTryAgain}
        />
      )}
      <Grid className={classes.orderListViewOuterContainer}>
        <Grid className={classes.orderListViewInnerContainer}>
          <FlexBox fullWidth flexOverride="none" margin="0 0 6" direction="row">
            <FlexBox direction="column">
              <Typography variant="h2">
                {(messaging && messaging.header) ||
                  `${t('commonText.hello')} ${customer.name}${t('commonText.exclamation')}`}
              </Typography>
              <Typography fontSize={30}>
                {(messaging && messaging.message) ||
                  (skipTicketList ? t('willCallOrderList.selectToPrint') : t('willCallOrderList.selectForInfo'))}
              </Typography>
            </FlexBox>
          </FlexBox>
          {tableView()}
        </Grid>
      </Grid>
      <Footer
        onPrevClick={() => setShowActionOverlay(true)}
        onNextClick={onNextClick}
        prevText={t('commonText.back')}
        nextText={skipTicketList ? t('commonText.printTickets') : t('commonText.next')}
        isNextDisabled={ordersSelected.length === 0}
        skipNavPrompt={Boolean(showActionOverlay || error)}
      />
      {showActionOverlay && (
        <ActionOverlay
          title={t('commonText.goBackTitle')}
          height={450}
          cancelText={t('commonText.goBack')}
          confirmText={t('commonText.cancel')}
          onCancel={onPreviousClick}
          onConfirm={() => setShowActionOverlay(false)}
        />
      )}
    </>
  );
};

export default WillCallOrderListView;
